import React from 'react';
import { 
  Sparkles, Mail, Phone, MapPin, ExternalLink, Code, Server, 
  Database, Cpu, Wrench, BookOpen, Briefcase, 
  Github, Linkedin, Globe, Layers, FileText
} from 'lucide-react';

const FormattedResumeSummary = ({ summaryText, title = "AI Resume Summary", application = null }) => {
  const effectiveSummaryText = typeof summaryText === 'string' && summaryText.trim() ? summaryText : (
    application ? (application.resume_summary || application.resume_text || application.skills || "No AI resume summary text available.") : ""
  );

  if (!effectiveSummaryText || typeof effectiveSummaryText !== 'string') {
    return (
      <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '0.88rem' }}>
        No AI resume summary text available.
      </div>
    );
  }

  // Helper to clean extracted text
  const cleanStr = (str) => str ? str.replace(/^[|•\s-]+|[|•\s-]+$/g, '').trim() : null;

  // 1. Extract contact & social links from raw text if present
  const emailMatch = effectiveSummaryText.match(/(?:Email:\s*|\b)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i);
  const phoneMatch = effectiveSummaryText.match(/(?:Mobile:\s*|Phone:\s*|\b)(\+?\d[\d\s-]{8,15}\d)/i);
  const linkedinMatch = effectiveSummaryText.match(/(?:LinkedIn:\s*|\b)((?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+)/i);
  const githubMatch = effectiveSummaryText.match(/(?:GitHub:\s*|\b)((?:https?:\/\/)?(?:www\.)?github\.com\/[a-zA-Z0-9_-]+)/i);
  const portfolioMatch = effectiveSummaryText.match(/(?:Portfolio:\s*|\b)((?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[a-zA-Z0-9_-]+)*)(?:\s|$)/i);
  
  let location = null;
  const locationMatch = effectiveSummaryText.match(/(?:Portfolio:[^\s]+\s+)([A-Z][a-zA-Z\s,]+?\d{6})/);
  if (locationMatch) {
    location = cleanStr(locationMatch[1]);
  } else {
    const locMatch2 = effectiveSummaryText.match(/([A-Za-z\s,]+?\s+\d{6})(?=\s+Professional Summary|\s+Summary|\s+Education)/i);
    if (locMatch2) location = cleanStr(locMatch2[1]);
  }

  const email = emailMatch ? cleanStr(emailMatch[1]) : null;
  const phone = phoneMatch ? cleanStr(phoneMatch[1]) : null;
  const linkedin = linkedinMatch ? cleanStr(linkedinMatch[1]) : null;
  const github = githubMatch ? cleanStr(githubMatch[1]) : null;
  const portfolio = portfolioMatch ? cleanStr(portfolioMatch[1]) : null;

  const hasContactInfo = email || phone || linkedin || github || portfolio || location;

  // 2. Extract sections using strict boundaries so random words like 'experience' or 'skills' inside sentences are never treated as headings
  const extractSections = (text) => {
    if (!text) return {};
    // Clean up hyphenated word wraps across newlines/spaces like "tem- perature" right at the start
    const cleanText = text
      .replace(/([a-zA-Z]+)-\s*\n?\s*([a-zA-Z]+)/g, '$1$2')
      // PDF extraction sometimes removes spaces from section headings.
      .replace(/\bProfessional\s*Summary\b/gi, 'Professional Summary')
      .replace(/\bTechnical\s*Skills\b/gi, 'Technical Skills')
      .replace(/\bWork\s*Experience\b/gi, 'Work Experience')
      .replace(/\bCore\s*Competencies\b/gi, 'Core Competencies');

    const headers = [
      { key: 'summary', regex: /\b(?:Professional\s+Summary|Summary|Objective)(?:\s*:|\s*\n|\s{2,}|$)/i },
      { key: 'skills', regex: /\b(?:Technical\s+Skills|Skills|Core\s+Competencies)(?:\s*:|\s*\n|\s{2,}|$)/i },
      { key: 'education', regex: /\b(?:Education|Academic\s+Details|Academic\s+Background)(?:\s*:|\s*\n|\s{2,}|\s+(?=[A-Z])|$)/i },
      { key: 'projects', regex: /\b(?:Projects|Featured\s+Projects|Academic\s+Projects)(?:\s*:|\s*\n|\s{2,}|\s+(?=[A-Z])|$)/i },
      { key: 'experience', regex: /\b(?:Work\s+Experience|Experience:|Internships|Employment\s+History)(?:\s*:|\s*\n|\s{2,}|$)/i }
    ];

    const found = [];
    headers.forEach(h => {
      const match = cleanText.match(h.regex);
      if (match) {
        found.push({ key: h.key, index: match.index, length: match[0].length });
      }
    });

    found.sort((a, b) => a.index - b.index);

    const sections = {};
    for (let i = 0; i < found.length; i++) {
      const current = found[i];
      const start = current.index + current.length;
      const end = (i + 1 < found.length) ? found[i + 1].index : cleanText.length;
      sections[current.key] = cleanText.substring(start, end).trim();
    }

    // If no summary header matched explicitly, check if text before the first section heading is the summary
    if (!sections.summary && found.length > 0 && found[0].index > 30) {
      let before = cleanText.substring(0, found[0].index).trim();
      if (portfolioMatch && portfolioMatch.index < found[0].index) {
        before = before.substring(portfolioMatch.index + portfolioMatch[0].length).trim();
        if (location && before.startsWith(location)) {
          before = before.substring(location.length).trim();
        }
      }
      if (before.length > 20) {
        sections.summary = before;
      }
    }

    return sections;
  };

  const sections = extractSections(effectiveSummaryText);
  let profSummary = sections.summary || null;

  if (profSummary) {
    // Clean up common PDF extraction artifacts and lingering URLs/emails
    profSummary = profSummary
      .replace(/https?:\/\/[^\s]+/gi, '') // Strip URLs first
      .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi, '') // Strip emails
      .replace(/\+?\d[\d\s-]{8,15}\d/gi, '') // Strip phone numbers
      .replace(/(?:\/envel~pe|\/linkedin|\/github|\/portfolio)\s*/gi, '') // Strip artifacts
      .trim();
    
    // If it's too short after cleaning, it was probably just contact info
    if (profSummary.length < 20) {
      profSummary = null;
    }
  }

  const techSkillsRaw = sections.skills || null;
  const educationRaw = sections.education || null;
  const projectsRaw = sections.projects || null;
  const experienceRaw = sections.experience || null;

  // 3. Parse Technical Skills categories
  const parseSkillCategories = (raw) => {
    if (!raw) return [];
    const categoryKeywords = [
      "Programming Languages",
      "Frameworks & Libraries",
      "Backend & APIs",
      "Databases & Cloud",
      "IoT & Embedded Systems",
      "Tools",
      "Other Skills",
      "Core Competencies",
      "Languages",
      "Frameworks",
      "Databases"
    ];

    const matches = [];
    categoryKeywords.forEach(kw => {
      const regex = new RegExp(`(${kw}\\s*:?)`, 'gi');
      let m;
      while ((m = regex.exec(raw)) !== null) {
        matches.push({ name: kw, index: m.index, length: m[0].length });
      }
    });

    matches.sort((a, b) => a.index - b.index);

    const cleanItems = (listStr) => {
      return listStr
        .split(/[,•|\n]+/)
        .map(x => x.replace(/^[:-]+/, '').replace(/[:-]+$/, '').trim())
        .filter(x => {
          if (!x) return false;
          const l = x.toLowerCase();
          const echoWords = [
            'languages', 'languages:', 'programming languages', 'frameworks',
            'frameworks & libraries', 'libraries', 'backend & apis', 'backend',
            'apis', 'databases & cloud', 'databases', 'cloud', 'iot & embedded systems',
            'iot', 'embedded systems', 'tools', 'other skills', 'core competencies',
            'none', 'n/a', 'nil', '-', '•', 'skill', 'skills'
          ];
          if (echoWords.includes(l)) return false;
          return true;
        });
    };

    if (matches.length === 0) {
      const items = cleanItems(raw);
      return items.length > 0 ? [{ category: "General Competencies", items }] : [];
    }

    const categories = [];
    for (let i = 0; i < matches.length; i++) {
      const cat = matches[i];
      const start = cat.index + cat.length;
      const end = (i + 1 < matches.length) ? matches[i + 1].index : raw.length;
      const itemsStr = raw.substring(start, end);
      const items = cleanItems(itemsStr);
      if (items.length > 0) {
        categories.push({ category: cat.name, items });
      }
    }
    return categories;
  };

  const skillCategories = parseSkillCategories(techSkillsRaw);

  // 4. Parse Education info
  const parseEducation = (raw) => {
    if (!raw) return null;
    const cgpaMatch = raw.match(/(?:CGPA:\s*|Percentage:\s*|GPA:\s*)([\d.]+(?:\/[\d]+)?(?:%)?)/i);
    const expectedMatch = raw.match(/(?:Expected|Graduation|Class of|Year):\s*([A-Za-z]+\s*\d{4}|\d{4}-\d{4}|\d{4})/i) || raw.match(/(\b202\d\b)/);
    const degreeMatch = raw.match(/(?:B\.E\.|B\.Tech|B\.Sc|M\.Tech|M\.Sc|Bachelor|Master|Diploma)[^\n,.]*/i);
    
    let inst = raw;
    if (degreeMatch && degreeMatch.index > 0) {
      inst = raw.substring(0, degreeMatch.index).replace(/[,•|-]+$/g, '').trim();
    } else if (expectedMatch && expectedMatch.index > 0) {
      inst = raw.substring(0, expectedMatch.index).replace(/[,•|-]+$/g, '').trim();
    }

    return {
      institution: inst || "Academic Institution",
      degree: degreeMatch ? degreeMatch[0].trim() : "Degree Program",
      expected: expectedMatch ? expectedMatch[0].replace(/^(?:Expected|Graduation|Class of|Year):\s*/i, '').trim() : null,
      cgpa: cgpaMatch ? cgpaMatch[1].trim() : null,
      raw
    };
  };

  const educationObj = parseEducation(educationRaw);

  // 5. Parse Projects info supporting multiple projects, clean bullet splitting, and intact tech stacks
  const parseProjects = (raw) => {
    if (!raw) return [];
    
    // Clean up hyphenated line breaks inside projects specifically and normalize spacing
    const cleanRaw = raw.replace(/([a-zA-Z]+)-\s*\n?\s*([a-zA-Z]+)/g, '$1$2').replace(/\r\n/g, '\n');

    // Identify distinct project blocks by matching titles separated by em-dash/en-dash/hyphen
    const projectBlocks = [];
    const titleRegex = /([A-Z][A-Za-z0-9\s()/\-+_&.#!]{2,60}?\s*[—–-]\s*[A-Z][A-Za-z0-9\s()/\-+_&.#!]{2,90}?)(?=\s*(?:Tech Stack|Stack|GitHub Repository|•|\n•|Developed|Implemented|Built|Integrated|Connected|Designed|Created|Used|Engineered|$))/gi;
    
    let match;
    const titleMatches = [];
    while ((match = titleRegex.exec(cleanRaw)) !== null) {
      titleMatches.push({ title: match[1].trim(), index: match.index });
    }

    if (titleMatches.length === 0) {
      projectBlocks.push({ title: "Featured Project", content: cleanRaw });
    } else {
      for (let i = 0; i < titleMatches.length; i++) {
        const start = titleMatches[i].index;
        const end = (i + 1 < titleMatches.length) ? titleMatches[i + 1].index : cleanRaw.length;
        projectBlocks.push({
          title: titleMatches[i].title,
          content: cleanRaw.substring(start, end).trim()
        });
      }
    }

    return projectBlocks.map(block => {
      // Extract Tech Stack across multiple items right up until first bullet symbol or action verb phrase
      let techStack = [];
      const stackMatch = block.content.match(/(?:Tech Stack|Stack)\s*:?\s*([^•]+?)(?=\s*•|\s*GitHub Repository|\s*(?:Developed|Implemented|Built|Integrated|Connected|Designed|Created|Used|Engineered)\b|\n\n|$)/i);
      if (stackMatch) {
        techStack = stackMatch[1].split(/[,|]+/).map(x => x.trim()).filter(Boolean);
      }

      // Remove the title and Tech Stack / GitHub links from content to isolate bullets/description
      let descStr = block.content;
      if (block.title !== "Featured Project") {
        descStr = descStr.replace(block.title, '');
      }
      descStr = descStr.replace(/(?:Tech Stack|Stack)\s*:?[^•]*(?=\s*•|$)/i, '')
                       .replace(/GitHub Repository\s*:?[^\n•]*/i, '')
                       .trim();

      // Split into initial bullet chunks
      let rawChunks = [];
      if (descStr.includes('•') || descStr.includes('●')) {
        rawChunks = descStr.split(/[•●]+/).map(x => x.replace(/\s+/g, ' ').trim()).filter(Boolean);
      } else if (descStr.includes('\n- ') || descStr.startsWith('- ')) {
        rawChunks = descStr.split(/(?:\n|^)-\s+/).map(x => x.replace(/\s+/g, ' ').trim()).filter(Boolean);
      } else {
        rawChunks = [descStr.replace(/\s+/g, ' ').trim()];
      }

      // Further split chunks into individual sentences if multiple sentences got lumped inside a single bullet
      const bullets = [];
      rawChunks.forEach(chunk => {
        if (!chunk) return;
        // Split sentence if we see `. ` followed by capital letter of an action verb or new thought
        const sentences = chunk.split(/(?<=\.)\s+(?=[A-Z])/);
        sentences.forEach(s => {
          const cleaned = s.replace(/\s+/g, ' ').trim();
          if (cleaned && !cleaned.toLowerCase().startsWith('tech stack') && !cleaned.toLowerCase().startsWith('github repository') && cleaned.length > 3) {
            bullets.push(cleaned);
          }
        });
      });

      return {
        title: block.title,
        techStack,
        bullets
      };
    });
  };

  const projectList = parseProjects(projectsRaw);

  const summaryPoints = profSummary
    ? profSummary
      .split(/[•●\n]+/)
      .flatMap(part => part.split(/(?<=\.)\s+(?=[A-Z])/))
      .map(point => point.replace(/\s+/g, ' ').trim())
      .filter(point => point.length > 8)
    : [];

  // Helper colors for skill category icons
  const getCategoryIcon = (catName) => {
    const l = catName.toLowerCase();
    if (l.includes('language')) return <Code size={15} style={{ color: '#3b82f6' }} />;
    if (l.includes('framework') || l.includes('librar')) return <Layers size={15} style={{ color: '#8b5cf6' }} />;
    if (l.includes('backend') || l.includes('api')) return <Server size={15} style={{ color: '#06b6d4' }} />;
    if (l.includes('database') || l.includes('cloud')) return <Database size={15} style={{ color: '#f59e0b' }} />;
    if (l.includes('iot') || l.includes('embedded')) return <Cpu size={15} style={{ color: '#ef4444' }} />;
    return <Wrench size={15} style={{ color: '#10b981' }} />;
  };

  const getCategoryColor = (idx) => {
    const colors = [
      { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb', border: 'rgba(59, 130, 246, 0.3)' },
      { bg: 'rgba(139, 92, 246, 0.12)', color: '#7c3aed', border: 'rgba(139, 92, 246, 0.3)' },
      { bg: 'rgba(6, 182, 212, 0.12)', color: '#0891b2', border: 'rgba(6, 182, 212, 0.3)' },
      { bg: 'rgba(245, 158, 11, 0.12)', color: '#d97706', border: 'rgba(245, 158, 11, 0.3)' },
      { bg: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.3)' },
      { bg: 'rgba(16, 185, 129, 0.12)', color: '#059669', border: 'rgba(16, 185, 129, 0.3)' },
    ];
    return colors[idx % colors.length];
  };

  return (
    <div style={{ 
      background: 'var(--faculty-bg, #ffffff)', 
      borderRadius: '14px', 
      border: '1px solid var(--faculty-border, #e5e7eb)', 
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)'
    }}>
      {/* Header Bar */}
      <div style={{ 
        padding: '16px 20px', 
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.08))', 
        borderBottom: '1px solid var(--faculty-border, #e5e7eb)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ 
            width: '32px', height: '32px', borderRadius: '8px', 
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#ffffff'
          }}>
            <Sparkles size={18} />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--faculty-text, #111827)' }}>
              {title}
            </h4>
            <span style={{ fontSize: '0.78rem', color: 'var(--faculty-muted, #6b7280)' }}>
              Extracted & structured by AI Resume Intelligence Engine
            </span>
          </div>
        </div>
      </div>

      {/* Quick Contact & Social Links Bar */}
      {hasContactInfo && (
        <div style={{ 
          padding: '14px 20px', 
          background: '#f8fafc', 
          borderBottom: '1px solid var(--faculty-border, #e5e7eb)',
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '10px',
          alignItems: 'center'
        }}>
          {email && (
            <a href={`mailto:${email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '0.8rem', color: '#334155', textDecoration: 'none', fontWeight: 500 }}>
              <Mail size={14} style={{ color: '#3b82f6' }} /> {email}
            </a>
          )}
          {phone && (
            <a href={`tel:${phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '0.8rem', color: '#334155', textDecoration: 'none', fontWeight: 500 }}>
              <Phone size={14} style={{ color: '#10b981' }} /> {phone}
            </a>
          )}
          {linkedin && (
            <a href={linkedin.startsWith('http') ? linkedin : `https://${linkedin}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '0.8rem', color: '#0284c7', textDecoration: 'none', fontWeight: 600 }}>
              <Linkedin size={14} /> Visit LinkedIn <ExternalLink size={12} />
            </a>
          )}
          {github && (
            <a href={github.startsWith('http') ? github : `https://${github}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '0.8rem', color: '#1e293b', textDecoration: 'none', fontWeight: 600 }}>
              <Github size={14} /> Visit GitHub <ExternalLink size={12} />
            </a>
          )}
          {portfolio && (
            <a href={portfolio.startsWith('http') ? portfolio : `https://${portfolio}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '0.8rem', color: '#7c3aed', textDecoration: 'none', fontWeight: 600 }}>
              <Globe size={14} /> Portfolio <ExternalLink size={12} />
            </a>
          )}
          {location && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '0.8rem', color: '#475569', fontWeight: 500 }}>
              <MapPin size={14} style={{ color: '#ef4444' }} /> {location}
            </span>
          )}
        </div>
      )}

      {/* Main Content Body */}
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* Professional Summary Section */}
        {profSummary && (
          <div style={{ 
            padding: '16px', 
            background: 'rgba(59, 130, 246, 0.05)', 
            borderLeft: '4px solid #3b82f6',
            borderRadius: '0 10px 10px 0',
            borderTop: '1px solid rgba(59, 130, 246, 0.15)',
            borderRight: '1px solid rgba(59, 130, 246, 0.15)',
            borderBottom: '1px solid rgba(59, 130, 246, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#1d4ed8', fontWeight: 700, fontSize: '0.9rem' }}>
              <FileText size={16} /> Professional Summary & Objective
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--faculty-text, #1e293b)', fontSize: '0.9rem', lineHeight: 1.6 }}>
              {(summaryPoints.length > 0 ? summaryPoints : [profSummary]).map((point, index) => (
                <li key={index} style={{ paddingLeft: '4px', marginBottom: index === summaryPoints.length - 1 ? 0 : '6px' }}>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Technical Skills Categorized Grid */}
        {skillCategories.filter(cat => cat && Array.isArray(cat.items) && cat.items.length > 0).length > 0 && (
          <div>
            <h5 style={{ margin: '0 0 12px 0', fontSize: '0.92rem', fontWeight: 700, color: 'var(--faculty-text, #334155)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Code size={16} style={{ color: '#3b82f6' }} /> Technical Competencies & Skill Matrix
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
              {skillCategories
                .filter(cat => cat && Array.isArray(cat.items) && cat.items.length > 0)
                .map((cat, idx) => {
                  const styleObj = getCategoryColor(idx);
                  return (
                    <div key={idx} style={{ 
                      padding: '14px', 
                      background: '#f8fafc', 
                      borderRadius: '10px', 
                      border: '1px solid var(--faculty-border, #e2e8f0)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 700, fontSize: '0.84rem', color: styleObj.color }}>
                        {getCategoryIcon(cat.category)} {cat.category}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {cat.items.map((skill, sIdx) => (
                          <span key={sIdx} style={{ 
                            background: styleObj.bg, 
                            color: styleObj.color, 
                            border: `1px solid ${styleObj.border}`,
                            padding: '3px 9px', 
                            borderRadius: '6px', 
                            fontSize: '0.78rem', 
                            fontWeight: 600 
                          }}>
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Education Highlight Card */}
        {educationObj && (
          <div style={{ 
            padding: '16px', 
            background: '#ffffff', 
            borderRadius: '10px', 
            border: '1px solid var(--faculty-border, #e2e8f0)' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#7c3aed', fontWeight: 700, fontSize: '0.9rem' }}>
              <BookOpen size={16} /> Academic Credentials & Education
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--faculty-text, #111827)' }}>
                  {educationObj.institution}
                </div>
                <div style={{ fontSize: '0.86rem', color: 'var(--faculty-muted, #475569)', marginTop: '4px' }}>
                  {educationObj.degree}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {educationObj.expected && (
                  <span style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700 }}>
                    Expected: {educationObj.expected}
                  </span>
                )}
                {educationObj.cgpa && (
                  <span style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '4px 10px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700 }}>
                    CGPA / Score: {educationObj.cgpa}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Work Experience Section */}
        {experienceRaw && (
          <div style={{ 
            padding: '16px', 
            background: '#ffffff', 
            borderRadius: '10px', 
            border: '1px solid var(--faculty-border, #e2e8f0)' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#15803d', fontWeight: 700, fontSize: '0.9rem' }}>
              <Briefcase size={16} /> Work Experience & Internships
            </div>
            <div style={{ fontSize: '0.88rem', color: 'var(--faculty-text, #334155)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {experienceRaw}
            </div>
          </div>
        )}

        {/* Unparsed fallback if no structured categories matched */}
        {!profSummary && skillCategories.length === 0 && !educationObj && !experienceRaw && (!projectList || projectList.length === 0) && (
          <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '10px', fontSize: '0.88rem', color: 'var(--faculty-text, #334155)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {effectiveSummaryText}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormattedResumeSummary;
